import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  useVideoMetadata,
  useVideoPlayback,
} from '../context/VideoEditorContext';
import { useDropTargetWithAutoScroll } from '../hooks/useDropTarget';
import { useSelectionAutoScroll } from '../hooks/useSelectionAutoScroll';
import { useVideoThumbnails } from '../hooks/useVideoThumbnails';
import { timeToAbsolutePixel } from '../utils/selectionCalculations';
import { VideoSelection } from '../utils/videoUtils';
import SelectionIndicator from './SelectionIndicator';
import Thumbnail from './Thumbnail';

interface FilmStripSliderProps {
  selection: VideoSelection;
  onSelectionChange: (selection: VideoSelection) => void;
  className?: string;
  onVideoTimeUpdate?: (time: number) => void;
}

export default function FilmStripSlider({
  selection,
  onSelectionChange,
  className = '',
  onVideoTimeUpdate,
}: FilmStripSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasInitiallyScrolled = useRef(false);

  const { videoSrc, videoMetadata } = useVideoMetadata();
  const { isPlaying, handlePlayPause } = useVideoPlayback();

  const src = videoSrc ?? '';

  // Unique instance ID for drag and drop isolation
  const [instanceId] = useState(() => Symbol('filmstrip-instance'));

  // Calculate if content is scrollable - use state to handle initial render
  const [isScrollable, setIsScrollable] = useState(true);

  const { thumbnails, isReady, totalContentWidth } = useVideoThumbnails({
    videoMetadata,
    stripHeight: 60, // Reasonable default height for loading state
    thumbnailsPerSecond: 0.5, // One thumbnail every 2 seconds
  });

  // Auto-scroll hook for initial render positioning
  const { ensureSelectionVisible } = useSelectionAutoScroll(
    scrollRef,
    isScrollable
  );

  const handleSelectionMove = useCallback(
    (startTime: number) => {
      const newEndTime = startTime + selection.duration;
      onSelectionChange({
        startTime,
        endTime: newEndTime,
        duration: selection.duration,
      });
    },
    [selection.duration, onSelectionChange]
  );

  useEffect(() => {
    if (containerRef.current && totalContentWidth > 0) {
      const containerWidth = containerRef.current.clientWidth;
      const newIsScrollable = totalContentWidth > containerWidth;
      setIsScrollable(newIsScrollable);
    }
  }, [totalContentWidth, videoMetadata?.duration, thumbnails.length]);

  // Auto-scroll to restored selection on initial render
  useEffect(() => {
    if (
      !hasInitiallyScrolled.current &&
      isReady &&
      videoMetadata &&
      containerRef.current &&
      selection &&
      totalContentWidth > 0
    ) {
      const containerWidth = containerRef.current.clientWidth;
      const selectionWidth = timeToAbsolutePixel(
        selection.duration,
        videoMetadata.duration,
        totalContentWidth
      );

      // Only scroll if selection might be outside viewport (non-zero start time)
      if (selection.startTime > 0) {
        ensureSelectionVisible(
          selection,
          videoMetadata.duration,
          totalContentWidth,
          selectionWidth,
          containerWidth
        );
      }

      hasInitiallyScrolled.current = true;
    }
  }, [
    isReady,
    videoMetadata,
    selection.startTime,
    selection.duration,
    totalContentWidth,
    ensureSelectionVisible,
  ]);

  // handles drop target registration for drag and drop functionality
  useDropTargetWithAutoScroll({
    scrollContainerRef: scrollRef,
    videoDuration: videoMetadata?.duration || 0,
    stripWidth: totalContentWidth,
    isScrollable,
    instanceId,
  });

  // Use actual thumbnail height, fallback to reasonable default
  const actualThumbnailHeight =
    thumbnails.length > 0 ? thumbnails[0].height : 60;

  if (!videoMetadata) {
    return (
      <div
        className={`relative bg-gray-800 rounded-lg flex items-center justify-center w-full ${className}`}
        style={{ height: 60 }}
      >
        <div className="text-white text-sm">Loading video metadata...</div>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div
        className={`relative bg-gray-800 rounded-lg flex items-center justify-center w-full ${className}`}
        style={{ height: 60 }}
      >
        <div className="text-white text-sm">Loading thumbnails...</div>
      </div>
    );
  }

  const selectionLabelBuffer = 40; //pixels

  return (
    <div className={`relative w-full ${className}`}>
      <div
        ref={containerRef}
        className="relative w-full overflow-visible"
        style={{
          height: actualThumbnailHeight + selectionLabelBuffer,
        }}
      >
        {/* Scrollable Filmstrip Container */}
        <div
          ref={scrollRef}
          className={
            isScrollable
              ? 'overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 h-full'
              : 'overflow-hidden h-full'
          }
        >
          {/* Filmstrip Content - Fixed width thumbnails */}
          <div
            className="flex"
            style={{
              width: totalContentWidth,
            }}
          >
            {thumbnails.map((thumbnail, index) => (
              <div
                key={`${src}-${thumbnail.timeInSeconds}-${index}`}
                className={`flex-shrink-0 ${
                  index === 0 ? 'overflow-hidden' : ''
                } ${index === thumbnails.length - 1 ? 'overflow-hidden' : ''}`}
                style={{
                  width: thumbnail.width,
                  height: thumbnail.height,
                }}
              >
                <Thumbnail src={src} thumbnail={thumbnail} />
              </div>
            ))}
          </div>

          {/* Selection Wrapper - positioned inside scrollable container for auto-scroll */}
          <SelectionIndicator
            selection={selection}
            videoDuration={videoMetadata.duration}
            stripWidth={totalContentWidth}
            stripHeight={actualThumbnailHeight}
            isScrollable={isScrollable}
            onSelectionMove={handleSelectionMove}
            onVideoTimeUpdate={onVideoTimeUpdate}
            scrollContainerRef={scrollRef}
            onPlayPause={handlePlayPause}
            instanceId={instanceId}
            isPlaying={isPlaying}
          />
        </div>
      </div>
    </div>
  );
}
